export interface FeatureFlags {
  clientsPage: boolean
  productsPage: boolean
  reportsPage: boolean
  todosPage: boolean
  schedulesPage: boolean
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  clientsPage: false,
  productsPage: false,
  reportsPage: false,
  todosPage: true,
  schedulesPage: true,
}

type FeatureFlagOverrides = Partial<Record<keyof FeatureFlags, unknown>>

const STAGING_HOSTNAME = 'staging.credem.streetbeat.com'
const STAGING_FORCED_HIDDEN_FLAGS: Array<keyof FeatureFlags> = [
  'clientsPage',
  'productsPage',
  'reportsPage',
]

export function parseFeatureFlags(raw: string | undefined): FeatureFlags {
  if (!raw?.trim()) return { ...DEFAULT_FEATURE_FLAGS }

  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...DEFAULT_FEATURE_FLAGS }
    }
    return mergeFeatureFlags(parsed as FeatureFlagOverrides)
  } catch {
    return { ...DEFAULT_FEATURE_FLAGS }
  }
}

export function mergeFeatureFlags(overrides: FeatureFlagOverrides | undefined): FeatureFlags {
  const flags = { ...DEFAULT_FEATURE_FLAGS }
  if (!overrides) return flags

  for (const key of Object.keys(flags) as Array<keyof FeatureFlags>) {
    const value = overrides[key]
    if (typeof value === 'boolean') flags[key] = value
  }
  return flags
}

export function getFeatureFlags(overrides?: FeatureFlagOverrides): FeatureFlags {
  return applyStagingFeatureFlagHardcode(
    mergeFeatureFlags({
      ...parseFeatureFlags(import.meta.env.VITE_CREDEM_FEATURE_FLAGS),
      ...overrides,
    }),
  )
}

export function applyStagingFeatureFlagHardcode(
  flags: FeatureFlags,
  hostname = currentHostname(),
): FeatureFlags {
  if (hostname !== STAGING_HOSTNAME) return flags

  return STAGING_FORCED_HIDDEN_FLAGS.reduce(
    (nextFlags, key) => ({
      ...nextFlags,
      [key]: false,
    }),
    { ...flags },
  )
}

function currentHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname
}
